import React, { useMemo } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";

/* Assistant message rendering.
 *
 * The agent answers in Markdown — it returns comparison tables, lists and bold
 * labels because that is the clearest way to present project data. Rendered as
 * plain text, a table becomes a wall of pipes, which is worse than no table.
 *
 * Styled with arbitrary child selectors rather than a `prose` class so the
 * table, list and code treatments match this module's design directly instead
 * of inheriting a generic article look.
 */

marked.setOptions({ gfm: true, breaks: true, async: false });

// Every element the agent can realistically emit gets a rule. Anything missed
// falls back to browser defaults, which look broken next to the styled rest.
const MD = [
  "[&_p]:my-0 [&_p+p]:mt-3 [&_p]:leading-relaxed",
  "[&_strong]:font-semibold [&_strong]:text-[#18101C]",
  "[&_em]:italic",
  "[&_a]:text-[#A04A97] [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-[#8B3F83]",
  "[&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:my-2 [&_ol]:pl-5 [&_ol]:list-decimal",
  "[&_li]:my-1 [&_li]:leading-relaxed [&_li>p]:my-0",
  "[&_h1]:text-[18px] [&_h2]:text-[17px] [&_h3]:text-[16px]",
  "[&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold",
  "[&_h1]:mt-4 [&_h2]:mt-4 [&_h3]:mt-3 [&_h1]:mb-1.5 [&_h2]:mb-1.5 [&_h3]:mb-1",
  "[&_h1]:text-[#18101C] [&_h2]:text-[#18101C] [&_h3]:text-[#18101C]",
  "[&_code]:rounded [&_code]:bg-[#18101C]/[0.06] [&_code]:px-1.5 [&_code]:py-0.5",
  "[&_code]:text-[13.5px] [&_code]:font-mono [&_code]:text-[#7A3573]",
  "[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-[#18101C] [&_pre]:p-3",
  "[&_pre]:text-[13.5px] [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-[#F4F4F5]",
  "[&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-[#A04A97]/40",
  "[&_blockquote]:pl-3 [&_blockquote]:text-[#5B5563]",
  "[&_hr]:my-4 [&_hr]:border-[#ECE9EE]",
  // Tables are the reason this component exists — project comparisons arrive as
  // tables constantly. Wrapped in an overflow container so a wide one scrolls
  // itself instead of stretching the whole chat column.
  "[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[14px]",
  "[&_thead_th]:bg-[#F4F1F6] [&_thead_th]:text-[12px] [&_thead_th]:uppercase",
  "[&_thead_th]:tracking-[0.06em] [&_thead_th]:text-[#6B6472] [&_thead_th]:font-semibold",
  "[&_th]:border [&_th]:border-[#ECE9EE] [&_th]:px-3 [&_th]:py-2 [&_th]:text-left",
  "[&_td]:border [&_td]:border-[#ECE9EE] [&_td]:px-3 [&_td]:py-2 [&_td]:align-top",
  "[&_tbody_tr:hover]:bg-[#FBF7FB]",
].join(" ");

/* ── citations ───────────────────────────────────────────────────────────────
 *
 * The agent grounds its claims in retrieved passages, and it used to say so in
 * the prose: "Evidence: 1764132541.pdf, page 9" under every bullet. That is the
 * right instinct and the wrong form. It costs a line per claim — three of them
 * and a four-line answer is a wall — and the file name is an upload timestamp,
 * so it tells the reader nothing while taking the width of a sentence to do it.
 *
 * A citation should be a MARK, not a line: small enough to disappear while
 * reading, precise enough to open the exact passage on click. So the model
 * writes [3] and this turns it into a chip wired to the source panel.
 *
 * WHY A NUMBER RATHER THAN A DOCUMENT GLYPH. An answer cites several passages,
 * and a row of identical icons cannot say which is which — you would have to
 * open all four to find the one you wanted. The number both marks and
 * identifies, which is why every product that does this converges on it.
 */

// Matches the mark the prompt asks for. Two digits is the whole range the
// server can issue, so a "[2024]" in quoted RFP prose can never be swallowed.
const CITE_RE = /\[(\d{1,2})\]/g;

/* Superscript, and deliberately quiet. A citation is read PAST, not read — it
 * earns attention only when the reader decides to check something, so it sits
 * at ten pixels in a tinted pill rather than competing with the sentence it
 * belongs to. Written as literal class strings because Tailwind only emits the
 * names it can see in source. */
const CHIP =
  "inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-[4px] " +
  "px-[3.5px] align-super text-[10px] font-semibold leading-none tabular-nums " +
  "bg-[#A04A97]/[0.11] text-[#8B3F83] transition-colors duration-150 " +
  "hover:bg-[#A04A97]/[0.22] hover:text-[#6F3269] focus:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none";

/* Turn every resolvable [n] into a chip.
 *
 * Done on the DOM rather than by string-replacing the HTML: a regex over markup
 * cannot tell prose from an attribute value, and the first URL containing [1]
 * would have a button spliced into the middle of its href. Walking text nodes
 * touches only text, by construction.
 *
 * An UNRESOLVABLE number is left exactly as it was. The model is told to cite
 * only what the current search returned, but if it ever cites a number that has
 * no passage, a dead chip that opens nothing is worse than the plain text — and
 * deleting it would quietly eat a "[1]" that was part of a quoted requirement.
 */
const decorate = (html, byNumber) => {
  if (!byNumber.size || !html.includes("[")) return html;

  const tpl = document.createElement("template");
  tpl.innerHTML = html;

  const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT);
  const targets = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (!node.nodeValue?.includes("[")) continue;
    // Code is quoted verbatim and a link already goes somewhere; neither wants
    // a button spliced into it.
    if (node.parentElement?.closest("pre, code, a")) continue;
    targets.push(node);
  }

  for (const node of targets) {
    const text = node.nodeValue;
    const frag = document.createDocumentFragment();
    let last = 0;
    let hit = false;

    CITE_RE.lastIndex = 0;
    let m = CITE_RE.exec(text);
    while (m) {
      const src = byNumber.get(Number(m[1]));
      if (src) {
        hit = true;
        if (m.index > last) frag.appendChild(document.createTextNode(text.slice(last, m.index)));

        const where = [src.file, src.page ? `page ${src.page}` : ""].filter(Boolean);
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = CHIP;
        btn.dataset.cite = String(src.n);
        /* Exempt from the panel's click-away close.
         *
         * The panel closes on any mousedown outside itself, and a chip IS
         * outside it — so clicking a second citation while the panel is open
         * would be read as "close" on mousedown and "open" on click, and the
         * panel would visibly slam shut and reopen to move one highlight.
         * Every other opener that lives outside the panel carries this for the
         * same reason. */
        btn.dataset.sensaKeepPanel = "true";
        /* Tooltip and label differ, in two ways that both matter.
         *
         * The tooltip names the document; the label says what the button DOES,
         * which is what a screen reader user needs before deciding to press it.
         * And they are punctuated differently: a middle dot is a VISUAL
         * separator that screen readers announce inconsistently — sometimes as
         * "middle dot", often as nothing at all, running the file name into the
         * page number. A comma is read as the pause it looks like. */
        btn.title = where.join(" · ") || "Open the source";
        btn.setAttribute(
          "aria-label",
          `Open source ${src.n}${where.length ? `: ${where.join(", ")}` : ""}`
        );
        btn.textContent = String(src.n);
        frag.appendChild(btn);

        last = m.index + m[0].length;
      }
      m = CITE_RE.exec(text);
    }

    if (!hit) continue;
    if (last < text.length) frag.appendChild(document.createTextNode(text.slice(last)));
    node.parentNode.replaceChild(frag, node);
  }

  return tpl.innerHTML;
};

const CopilotMessage = ({ text, streaming, sources, onCite }) => {
  const byNumber = useMemo(() => {
    const map = new Map();
    for (const s of sources || []) if (s?.n) map.set(Number(s.n), s);
    return map;
  }, [sources]);

  const html = useMemo(() => {
    const raw = String(text || "");
    if (!raw.trim()) return "";
    try {
      // Sanitize always: this is model output rendered as HTML, and a prompt
      // injection that reaches the DOM is a real attack, not a hypothetical.
      // Chips are added AFTER, so the sanitizer never has to be loosened to
      // permit the one element we inject ourselves.
      return decorate(DOMPurify.sanitize(marked.parse(raw)), byNumber);
    } catch {
      return DOMPurify.sanitize(raw.replace(/</g, "&lt;"));
    }
  }, [text, byNumber]);

  /* One listener for every chip in the message.
   *
   * The prose is set through dangerouslySetInnerHTML, so the buttons inside it
   * are not React's to bind — delegation is the only way to hear them, and it
   * costs one handler instead of one per citation. */
  const handleClick = (e) => {
    const btn = e.target.closest?.("[data-cite]");
    if (!btn) return;
    e.preventDefault();
    const n = Number(btn.dataset.cite);
    if (n) onCite?.(n);
  };

  return (
    <div className={`min-w-0 overflow-x-auto text-[15.5px] text-[#18101C] ${MD}`}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
      <div onClick={handleClick} dangerouslySetInnerHTML={{ __html: html }} />
      {/* A caret while tokens land, so a pause mid-answer reads as "still
          writing" rather than "finished, badly".
          2px wide, not 7 — at seven it is a solid block sitting in the prose,
          which reads as a rendering fault rather than a cursor. */}
      {streaming && (
        <span className="ml-[3px] inline-block h-[16px] w-[2px] translate-y-[3px] rounded-full bg-[#A04A97]/80 animate-pulse motion-reduce:animate-none" />
      )}
    </div>
  );
};

export default CopilotMessage;
